import { useState } from 'react';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6dr09rv53dtlm7j8q7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const quickLinks = [
    { name: '회사소개', id: 'company' },
    { name: '서비스', id: 'services' },
    { name: '제품 포트폴리오', id: 'products' },
    { name: '파트너십', id: 'partnerships' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-gray-900">
      {/* Contact Form Section */}
      <div id="contact" className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <p className="text-xs font-semibold text-amber-600 tracking-widest uppercase mb-4">
              CONTACT US
            </p>
            
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              프로젝트를<br />
              시작해보세요
            </h2>

            <p className="text-lg text-gray-400 mb-12">
              브랜드의 성공을 위한 첫 걸음, 글로리브가 함께합니다
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-600/10 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">전화 문의</div>
                  <div className="text-lg font-medium text-white">010-2239-2737</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-600/10 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">이메일</div>
                  <div className="text-lg font-medium text-white">GLORIVE0722@naver.com</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-600/10 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">주소</div>
                  <div className="text-lg font-medium text-white">서울특별시 송파구 위례광장로 199 성희프라자 501-1-15호</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                    이름 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 transition-colors"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 transition-colors"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 transition-colors"
                    placeholder="010-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-2">
                    회사명
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 transition-colors"
                    placeholder="회사명 (선택)"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                  문의 내용 *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 transition-colors resize-none"
                  placeholder="문의하실 내용을 입력해주세요 (최대 500자)"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '전송 중...' : '상담 신청하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-400">문의가 성공적으로 전송되었습니다. 빠른 시일 내에 연락드리겠습니다.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">전송 중 오류가 발생했습니다. 다시 시도해주세요.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Column 1 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-600 to-amber-800"></div>
                </div>
                <h3 className="text-xl font-serif font-bold text-white">GLORIVE</h3>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                화장품 OEM/ODM 전문 기업<br />
                브랜드 가치를 완성하는<br />
                원스톱 뷰티 솔루션
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Contact</h4>
              <ul className="space-y-3">
                <li className="text-sm text-gray-400">010-2239-2737</li>
                <li className="text-sm text-gray-400">GLORIVE0722@naver.com</li>
                <li className="text-sm text-gray-400">서울특별시 송파구<br />위례광장로 199<br />성희프라자 501-1-15호</li>
              </ul>
            </div>

            {/* Column 4 */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Follow Us</h4>
              <div className="flex gap-3">
                <a
                  href="#"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-amber-600 transition-colors"
                >
                  <i className="ri-linkedin-fill text-lg text-white"></i>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-amber-600 transition-colors"
                >
                  <i className="ri-instagram-line text-lg text-white"></i>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-amber-600 transition-colors"
                >
                  <i className="ri-youtube-fill text-lg text-white"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Large Text */}
      <div className="border-t border-gray-800 overflow-hidden py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-8xl lg:text-9xl font-serif font-bold text-amber-900/20 tracking-wider text-center">
            GLORIVE
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              © 2024 GLORIVE. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-xs text-gray-500 hover:text-white transition-colors whitespace-nowrap">
                이용약관
              </a>
              <a href="#" className="text-xs text-gray-500 hover:text-white transition-colors whitespace-nowrap">
                개인정보처리방침
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-white transition-colors whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
